import { useMemo, Suspense, useEffect, useCallback, useState } from 'react'
// import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { projectCategories, collectiveCategories } from '@/lib/constants'
import { useLocation, useNavigate } from 'react-router-dom'
import Navigation from './Navigation'
import TopNav from './TopNav'
import { Loader2 } from 'lucide-react'
import { ErrorBoundary } from 'react-error-boundary'
import useUIStore from '@/store/useUIStore'

export default function MainLayout({
  children
}: {
  children: React.ReactNode
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Define paths where the content should be viewport-centered (not affected by sidebar padding)
  const centeredPaths = useMemo(() => [
    '/projects/new',
    '/settings',
    '/settings/profile',
    '/beta-testing',
    '/profile',
  ], []);

  const isCenteredPage = centeredPaths.includes(location.pathname) || location.pathname.startsWith('/messages/');

  const { setIsTypesOpen } = useUIStore();

  // Reset all state when location changes
  useEffect(() => {
    const cleanup = () => {
      setIsPageLoading(false)
      setIsTransitioning(false)
      setMobileSidebarOpen(false)
      setIsTypesOpen(false)
    }

    // Start transition immediately
    setIsTransitioning(true)
    setIsPageLoading(true)

    // Use Promise.resolve().then to ensure state updates happen after current render
    Promise.resolve().then(() => {
      // Add a small delay for loading state
      const loadingTimer = setTimeout(() => {
        setIsPageLoading(false)
      }, 100)

      // Add a slightly longer delay for transition state
      const transitionTimer = setTimeout(() => {
        setIsTransitioning(false)
      }, 300)

      return () => {
        cleanup()
        clearTimeout(loadingTimer)
        clearTimeout(transitionTimer)
      }
    })

    return cleanup
  }, [location.pathname, setIsTypesOpen])

  // Handle navigation errors
  const handleNavigationError = useCallback(() => {
    // Force navigate to explore page as fallback
    navigate('/explore', { replace: true })
  }, [navigate])

  // Dynamic navbar content based on current page
  const currentPageConfig = useMemo(() => {
    const basePath = location.pathname.split('/')[1]

    switch (basePath) {
      case 'explore':
        return {
          title: 'Explore',
          searchPlaceholder: 'Search projects...',
          categories: projectCategories,
          showTypes: true,
          showFilters: true
        }
      case 'library':
        return {
          title: 'Library',
          searchPlaceholder: 'Search media...',
          categories: ['All', 'Audio', 'Video', 'Images', 'Saved'],
          showTypes: true,
          showFilters: true
        }
      case 'projects':
        // Handle both /projects and /projects/:id
        if (location.pathname === '/projects/new') {
             return { title: 'New Project', searchPlaceholder: '', categories: [], showTypes: false, showFilters: false };
        }
        return {
          title: 'Projects',
          searchPlaceholder: 'Search your projects...',
          categories: projectCategories,
          showTypes: true,
          showFilters: true
        }
      case 'collectives':
        return {
          title: 'Collectives',
          searchPlaceholder: 'Search collectives...',
          categories: collectiveCategories,
          showTypes: true,
          showFilters: true
        }
      case 'events':
        return {
          title: 'Events',
          searchPlaceholder: 'Search events...',
          categories: ['All', 'Hackathons', 'Competitions', 'Workshops', 'Meetups'],
          showTypes: true,
          showFilters: true
        }
      case 'queue':
        return {
          title: 'Queue',
          searchPlaceholder: 'Search queue...',
          categories: ['All', 'Projects', 'Collaborations'],
          showTypes: true,
          showFilters: true
        }
      case 'beta-testing':
         return { title: 'Beta Testing & Feedback', searchPlaceholder: '', categories: [], showTypes: false, showFilters: false };
      case 'settings':
        // Handle both /settings and /settings/profile
        if (location.pathname === '/settings/profile') {
            return { title: 'Edit Profile', searchPlaceholder: '', categories: [], showTypes: false, showFilters: false };
        }
         return { title: 'Settings', searchPlaceholder: '', categories: [], showTypes: false, showFilters: false };
      case 'profile':
         return { title: 'Profile', searchPlaceholder: '', categories: [], showTypes: false, showFilters: false };
      default:
        return {
          title: '',
          searchPlaceholder: 'Search...',
          categories: [],
          showTypes: false,
          showFilters: false
        }
    }
  }, [location.pathname])

  return (
    <div className='min-h-screen relative'>
      <div className='relative flex'>
        <Navigation
          mobileSidebarOpen={mobileSidebarOpen}
          setMobileSidebarOpen={setMobileSidebarOpen}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
        />

        {/* Main content area wrapper */}
        <div
          className={cn(
            'flex-1 transition-all duration-300 ease-in-out max-w-full'
          )}
        >
          <TopNav
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            currentPageConfig={currentPageConfig}
            setMobileSidebarOpen={setMobileSidebarOpen}
            className={cn(
              'transition-[padding-left] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
              isExpanded ? 'lg:pl-64' : 'lg:pl-20'
            )}
          />

          {/* Page content area */}
          <div className={cn(
              'py-4 sm:py-6 lg:py-8 px-4 sm:px-6',
              isCenteredPage ? 'lg:px-4' : 'lg:px-8',
              !isCenteredPage && (isExpanded ? 'lg:pl-72' : 'lg:pl-28'),
              'transition-[padding-left] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]'
          )}>
            {isPageLoading || isTransitioning ? (
              <div className="absolute min-h-screen inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="flex min-h-screen items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  </div>
                }
              >
                <ErrorBoundary
                  onError={handleNavigationError}
                  fallbackRender={({ error }) => (
                    <div className="flex flex-col items-center justify-center h-screen">
                      <h1 className="text-2xl font-bold text-red-500">Something went wrong</h1>
                      <p className="text-gray-500 mt-2">{(error as Error).message}</p>
                    </div>
                  )}
                >
                  {children}
                </ErrorBoundary>
              </Suspense>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}
